import React, { useEffect, useRef, useState } from 'react'
import {
  ArrowDownTrayIcon,
  ArrowsRightLeftIcon,
  EllipsisHorizontalIcon,
  PencilSquareIcon,
  TableCellsIcon,
  TrashIcon
} from '@heroicons/react/24/outline'
import { IWM_ROOT_MAPPING_BUTTON_LABEL } from '../../../utils/pqr/swapIwmBugType'

export interface WeldParametersOverflowMenuProps {
  showEdit?: boolean
  onEdit?: () => void
  showDelete?: boolean
  onDelete?: () => void
  deleteDisabled?: boolean
  onColumns: () => void
  /** When false, hide Configure IWM Root mapping (e.g. Semi-Automatic Root Misfire). */
  showConfigureIwmRoot?: boolean
  onConfigureIwmRoot?: () => void
  iwmBusy?: boolean
  onExportExcel: () => void
  exportBusy?: boolean
}

/**
 * Data360-style ⋯ menu for Weld Parameters secondary actions.
 */
export default function WeldParametersOverflowMenu({
  showEdit = false,
  onEdit,
  showDelete = false,
  onDelete,
  deleteDisabled = false,
  onColumns,
  showConfigureIwmRoot = true,
  onConfigureIwmRoot,
  iwmBusy = false,
  onExportExcel,
  exportBusy = false
}: WeldParametersOverflowMenuProps) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const itemClass =
    'flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-text-grey hover:bg-page-bg disabled:cursor-not-allowed disabled:opacity-50'

  return (
    <div className="relative" ref={rootRef} data-testid="wp-overflow-menu">
      <button
        type="button"
        data-testid="wp-overflow-menu-btn"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center justify-center rounded-md border border-[#E2E5EA] bg-white p-1.5 text-heritage-grey shadow-sm transition-colors hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700"
      >
        <EllipsisHorizontalIcon className="h-4 w-4" aria-hidden />
        <span className="sr-only">More actions</span>
      </button>
      {open ? (
        <div
          role="menu"
          data-testid="wp-overflow-menu-panel"
          className="absolute right-0 z-40 mt-1.5 min-w-[220px] overflow-hidden rounded-lg border border-[#E2E5EA] bg-white py-1 shadow-lg"
        >
          {showEdit && onEdit ? (
            <button
              type="button"
              role="menuitem"
              data-testid="wp-overflow-edit"
              className={itemClass}
              onClick={() => {
                setOpen(false)
                onEdit()
              }}
            >
              <PencilSquareIcon className="h-4 w-4 shrink-0" aria-hidden />
              Edit
            </button>
          ) : null}
          {showDelete && onDelete ? (
            <button
              type="button"
              role="menuitem"
              data-testid="wp-overflow-delete"
              disabled={deleteDisabled}
              className={itemClass}
              onClick={() => {
                setOpen(false)
                onDelete()
              }}
            >
              <TrashIcon className="h-4 w-4 shrink-0" aria-hidden />
              Delete upload
            </button>
          ) : null}
          <button
            type="button"
            role="menuitem"
            data-testid="wp-overflow-columns"
            className={itemClass}
            onClick={() => {
              setOpen(false)
              onColumns()
            }}
          >
            <TableCellsIcon className="h-4 w-4 shrink-0" aria-hidden />
            Columns
          </button>
          {showConfigureIwmRoot && onConfigureIwmRoot ? (
            <button
              type="button"
              role="menuitem"
              data-testid="wp-overflow-iwm-root"
              disabled={iwmBusy}
              className={itemClass}
              onClick={() => {
                setOpen(false)
                onConfigureIwmRoot()
              }}
            >
              <ArrowsRightLeftIcon className="h-4 w-4 shrink-0" aria-hidden />
              {IWM_ROOT_MAPPING_BUTTON_LABEL}
            </button>
          ) : null}
          <button
            type="button"
            role="menuitem"
            data-testid="wp-overflow-export-excel"
            disabled={exportBusy}
            className={itemClass}
            onClick={() => {
              setOpen(false)
              onExportExcel()
            }}
          >
            <ArrowDownTrayIcon className="h-4 w-4 shrink-0" aria-hidden />
            {exportBusy ? 'Exporting…' : 'Export Excel'}
          </button>
        </div>
      ) : null}
    </div>
  )
}
